import { useMemo } from 'react';

import flatMap from 'lodash/flatMap';
import map from 'lodash/map';
import sumBy from 'lodash/sumBy';

import { attachTimeTicks } from 'utils/chart';
import { fillGaps } from 'utils/metrics';

import { OOMKillTotals } from '../types';
import { fillerFn, mergeOomKillDataPoints } from '../utils';
import { OomKillBucketQueryRange } from './useOomKillBucketWorkloadsQuery';

import { useCluster } from '@cast/shared-cluster-context-data-access';

import { useWorkloadEventMetricsQuery } from '@cast/shared-cost-data-access';

const MANAGED_BY_WOOP = 'woop';

type UseOomKillEventMetricsChartDataArgs = {
  rangeSelection: OomKillBucketQueryRange;
  enabled?: boolean;
};

/**
 * Cluster OOM chart series from workload event metrics with an injected time
 * range (OneClick dashboard). Console keeps using useWorkloadEventMetricsData.
 */
export const useOomKillEventMetricsChartData = ({
  rangeSelection,
  enabled = true,
}: UseOomKillEventMetricsChartDataArgs) => {
  const { cluster } = useCluster();
  const { getRange, key, stepSeconds } = rangeSelection;
  const range = getRange();

  const {
    data: series,
    isLoading,
    isError,
    refetch,
  } = useWorkloadEventMetricsQuery({
    clusterId: cluster.id,
    startTime: range.from,
    endTime: range.to,
    eventTypes: ['OOMKilled'],
    stepSeconds,
    groupByManagedBy: true,
    key,
    enabled,
  });

  const rawItems = useMemo(
    () =>
      flatMap(series?.filter((s) => s.eventType === 'OOMKilled') ?? [], (o) => {
        const isManaged = o.managedBy === MANAGED_BY_WOOP;

        return map(o.items ?? [], (item) => ({
          timestamp: item.timestamp,
          managedCount: isManaged ? parseInt(item.eventCount, 10) : null,
          unmanagedCount: isManaged ? null : parseInt(item.eventCount, 10),
        }));
      }),
    [series]
  );

  const totals = useMemo((): OOMKillTotals => {
    const managed = sumBy(rawItems, 'managedCount');
    const unmanaged = sumBy(rawItems, 'unmanagedCount');
    return { managed, unmanaged, total: managed + unmanaged };
  }, [rawItems]);

  const chartData = useMemo(() => {
    const mergedItems = mergeOomKillDataPoints(rawItems);
    if (mergedItems.length === 0) {
      return [];
    }
    const filled = fillGaps({
      timeSeries: mergedItems,
      dateRange: range,
      filler: fillerFn,
      unit: 'second',
      step: stepSeconds,
    });

    return attachTimeTicks(filled);
  }, [rawItems, range, stepSeconds]);

  return {
    chartData,
    isLoading,
    isError,
    refetch,
    totals,
    stepSeconds,
  };
};
